"use client"

import {
  useFormContext,
  Controller,
  type FieldValues,
  type Path,
} from "react-hook-form"
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input"
import flags from "react-phone-number-input/flags"
import "react-phone-number-input/style.css"
import { cn } from "@/lib/utils"

interface RHFPhoneInputProps<T extends FieldValues> {
  name: Path<T>
  label: string
  defaultCountry?:
    | "AE"
    | "IN"
    | "PK"
    | "GB"
    | "US"
    | "SA"
    | "QA"
    | "KW"
    | "BH"
    | "OM"
  required?: boolean
}

export function RHFPhoneInput<T extends FieldValues>({
  name,
  label,
  defaultCountry = "AE",
  required,
}: RHFPhoneInputProps<T>) {
  const { control } = useFormContext<T>()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-nova-800 dark:text-white">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
          <PhoneInput
            international
            flags={flags}
            defaultCountry={defaultCountry}
            value={(field.value as string) || undefined}
            onChange={(value) => field.onChange(value || "")}
            className={cn(
              "rhf-phone-input w-full rounded-lg border bg-white px-4 py-2.5 text-sm text-nova-800",
              "focus-within:border-nova-600 focus-within:ring-2 focus-within:ring-nova-600/20",
              "dark:border-slate-700 dark:bg-slate-900 dark:text-white",
              error
                ? "border-red-500 focus-within:border-red-500 focus-within:ring-red-500/20"
                : "border-nova-200"
            )}
          />
          {error && <p className="text-xs text-red-500">{error.message}</p>}
        </div>
      )}
    />
  )
}

export { isValidPhoneNumber }
